/**
 * ELAL GA4 event contract (EL AL - Travel Web Implementation Doc).
 * Builds dl_event, dl_page and user-data payloads for the ELAL web container and native WebView bridge.
 */

import {
    toNumber,
    roundMoney,
    pickString,
    normalizeDate,
    hashMatmidId,
    getAdultsCount,
    getChildrenCount,
    getGuestsCount,
    getTripLength,
    getDaysToTrip
} from './ga4-ecommerce';
import FiltersConstants from '../../../constants/FiltersConstants';
import LoyaltyService from '../../services/loyaltyService';
import InteractionConstants from '../../constants/interactions';
import RegionService from '../../services/regionService';
import FieldValidationService from '../../services/fieldValidationService';
import ListVersionService from '../../services/listVersionService';
import RevenueService from '../../services/revenueService';
import PaymentInfoService from '../../services/paymentInfoService';
import { SUPPORTED_LANGUAGES } from '../../../../translation/const';

const { FILTER_METHOD } = FiltersConstants;
const { SELECTED_OPTION, POPUP_NAME } = LoyaltyService;
const {
    TERMS_BUTTON_TYPE,
    HOTEL_SELECTION_SOURCE,
    HOTEL_VIEW_TRIGGER,
    PAGE_PROCESS,
    ERROR_TYPE,
    FUNNEL_STEP,
    BOOKING_PANEL_COMPONENT
} = InteractionConstants;
const { getDestinationRegion } = RegionService;
const { isNonEmptyFieldValue, getInvalidFieldErrorText } = FieldValidationService;
const { PAYMENT_INFO_TYPE, PAYMENT_DECLINE_TYPE, normalizeCardBrand, sanitizeDeclineText } = PaymentInfoService;

const DL_EVENT = 'dl_event';
const DL_PAGE = 'dl_page';
const GROUP_ELAL_TRAVEL = 'elal_travel';
const IS_NEW_IMPLEMENTATION = 'yes';
const VALUE_NA = 'NA';

const PROCESS_HOTEL_RESERVATION = PAGE_PROCESS.HOTEL_RESERVATION;
const PROCESS_LOGIN = PAGE_PROCESS.LOGIN;
const PROCESS_MY_HOTELS = PAGE_PROCESS.MY_HOTELS;
const PROCESS_EARLY_PAYMENT = 'early_payment';
const PAGE_PROCESSES = Object.values(PAGE_PROCESS);

const ELAL_PAYLOAD_KIND = {
    EVENT: 'event',
    PAGE: 'page',
    USER_DATA: 'user_data'
};

const NATIVE_MESSAGE_TYPE_EVENT = 'firebase_event';
const NATIVE_MESSAGE_TYPE_USER_DATA = 'firebase_user_data';
const NATIVE_SCREEN_VIEW = 'screen_view';

const LANGUAGE_NAME_BY_CODE = {
    [SUPPORTED_LANGUAGES.EN]: 'English',
    [SUPPORTED_LANGUAGES.HE]: 'Hebrew'
};

const MEMBER_TYPE_MATMID = 'matmid';

const POINTS_RANGE_ZERO = '0 points';
const POINTS_RANGE_TOP = '100000 and up';
// Upper bounds are inclusive; the contract's "33000 to 35000" label is kept verbatim for 33001-35000.
const POINTS_RANGES = [
    { max: 3000, label: '1 to 3000' },
    { max: 8000, label: '3001 to 8000' },
    { max: 13000, label: '8001 to 13000' },
    { max: 18000, label: '13001 to 18000' },
    { max: 23000, label: '18001 to 23000' },
    { max: 28000, label: '23001 to 28000' },
    { max: 33000, label: '28001 to 33000' },
    { max: 35000, label: '33000 to 35000' },
    { max: 60000, label: '35001 to 60000' },
    { max: 100000, label: '60001 to 100000' }
];

const COMPONENT_BOOKING_PANEL = 'booking_panel';
const EVENT_DETAIL_PAY_NOW = 'pay_now';
const BOOKING_PANEL_COMPONENTS = Object.values(BOOKING_PANEL_COMPONENT);

const ERROR_TYPES = Object.values(ERROR_TYPE);
const MAX_ERROR_TEXT_LENGTH = 100;
// Emails and long digit runs (phones, cards, IDs) must never reach analytics.
const RE_SENSITIVE_ERROR_TEXT = /@|\d{5,}/;

const ENTRANCE_POINT_TRAVEL = 'travel';

const ITEM_CATEGORY_HOTELS = 'hotels';
const ITEM_QUANTITY = 1;
const ITEM_LIST_HOTELS_RESULTS = 'hotels_results';
const ITEM_LIST_MAP = 'map';
const MAX_LIST_ITEMS = 20;
const SINGLE_CURRENCY_COUNT = 1;

const DATE_FORMAT_SHORT = 'DD/MM/YY';
const DATE_FORMAT_YEAR = 'YYYY';
const DATE_FORMAT_MONTH = 'MM';

const STEP_SEARCH_HOTEL = { step_in_funnel: '1', step_name: 'search_hotel' };
const STEP_HOTEL_LIST = { step_in_funnel: '2', step_name: 'hotel_select' };
const STEP_SELECT_HOTEL = { step_in_funnel: '3', step_name: 'hotel_select' };
const STEP_VIEW_HOTEL = { step_in_funnel: '4', step_name: 'hotel_select' };
const STEP_ROOM_SELECT = { step_in_funnel: '5', step_name: 'room_select' };
const STEP_PAYMENT = { step_in_funnel: '6', step_name: 'payment' };
const STEP_PAYMENT_INFO = { step_in_funnel: '7', step_name: 'payment' };
const STEP_CONFIRMATION = { step_in_funnel: '8', step_name: 'confirmation' };
const STEP_PAY_NOW = { step_in_funnel: '1', step_name: 'pay_now' };

const STEP_BY_FUNNEL_STEP = {
    [FUNNEL_STEP.SEARCH_HOTEL]: STEP_SEARCH_HOTEL,
    [FUNNEL_STEP.HOTEL_LIST]: STEP_HOTEL_LIST,
    [FUNNEL_STEP.SELECT_HOTEL]: STEP_SELECT_HOTEL,
    [FUNNEL_STEP.VIEW_HOTEL]: STEP_VIEW_HOTEL,
    [FUNNEL_STEP.ROOM_SELECT]: STEP_ROOM_SELECT,
    [FUNNEL_STEP.PAYMENT]: STEP_PAYMENT,
    [FUNNEL_STEP.CONFIRMATION]: STEP_CONFIRMATION
};

const SORT_DETAIL_RECOMMENDED = 'Recommended';
const SORT_DETAIL_PRICE_LOW_TO_HIGH = 'Price (Low to High)';
const SORT_DETAIL_PRICE_HIGH_TO_LOW = 'Price (High to Low)';
const SORT_DETAIL_DISTANCE = 'Distance from center';
const SORT_DETAIL_REVIEW_SCORE = 'Review Score';

// Keys are lowercased sort values sent by web (SortConstants.SORT_METHOD) and mobile (HotelSortFilter).
const SORT_DETAIL_BY_METHOD = {
    recommended: SORT_DETAIL_RECOMMENDED,
    price: SORT_DETAIL_PRICE_LOW_TO_HIGH,
    'price (low to high)': SORT_DETAIL_PRICE_LOW_TO_HIGH,
    'price (high to low)': SORT_DETAIL_PRICE_HIGH_TO_LOW,
    distance: SORT_DETAIL_DISTANCE,
    review: SORT_DETAIL_REVIEW_SCORE,
    'review score': SORT_DETAIL_REVIEW_SCORE
};

const FILTER_DETAIL_DISTANCE = 'Max Distance from Destination';
const FILTER_DETAIL_HOTEL_NAME = 'Hotel Name';
const FILTER_DETAIL_CATEGORIES = 'Categories';
const FILTER_DETAIL_PRICE = 'Maximum Price Per Night';
const FILTER_DETAIL_AMENITIES = 'Amenities';
const FILTER_DETAIL_STARS = 'Stars';
const FILTER_DETAIL_REVIEW_SCORE = 'Review Score';
const FILTER_DETAIL_AVAILABILITY = 'Availability';

// Travel-policy and preferred-hotel filters have no ELAL label and fall back to NA.
const FILTER_DETAIL_BY_METHOD = {
    [FILTER_METHOD.DISTANCE]: FILTER_DETAIL_DISTANCE,
    [FILTER_METHOD.HOTEL]: FILTER_DETAIL_HOTEL_NAME,
    [FILTER_METHOD.CATEGORIES]: FILTER_DETAIL_CATEGORIES,
    [FILTER_METHOD.CATEGORY_ONLY]: FILTER_DETAIL_CATEGORIES,
    [FILTER_METHOD.RESET_CATEGORIES_FILTER]: FILTER_DETAIL_CATEGORIES,
    [FILTER_METHOD.PRICE]: FILTER_DETAIL_PRICE,
    [FILTER_METHOD.AMENITIES]: FILTER_DETAIL_AMENITIES,
    [FILTER_METHOD.AMENITY_ONLY]: FILTER_DETAIL_AMENITIES,
    [FILTER_METHOD.RESET_AMENITIES_FILTER]: FILTER_DETAIL_AMENITIES,
    [FILTER_METHOD.STARS]: FILTER_DETAIL_STARS,
    [FILTER_METHOD.STARS_ONLY]: FILTER_DETAIL_STARS,
    [FILTER_METHOD.REVIEW]: FILTER_DETAIL_REVIEW_SCORE,
    [FILTER_METHOD.REVIEW_ONLY]: FILTER_DETAIL_REVIEW_SCORE,
    [FILTER_METHOD.AVAILABILITY]: FILTER_DETAIL_AVAILABILITY
};

const POPUP_CHOICE_BY_LOGIN_OPTION = {
    [SELECTED_OPTION.LOGIN]: 'login',
    [SELECTED_OPTION.REGISTER]: 'signup'
};

const TERMS_BUTTON_TYPES = Object.values(TERMS_BUTTON_TYPE);
const PAYMENT_INFO_TYPES = Object.values(PAYMENT_INFO_TYPE);
const PAYMENT_DECLINE_TYPES = Object.values(PAYMENT_DECLINE_TYPE);
const TERMS_COMPONENT_ARBITRIP = 'arbitrip';

function toNA(value) {
    return value === null || value === undefined || value === '' ? VALUE_NA : value;
}

function formatDateAs(date, format) {
    const normalized = normalizeDate(date);
    return normalized ? normalized.format(format) : null;
}

function buildElalEvent(eventName, process, eventDetail, extraFields = {}) {
    return {
        event: DL_EVENT,
        event_name: eventName,
        group: GROUP_ELAL_TRAVEL,
        process,
        event_detail: toNA(eventDetail),
        ...extraFields,
        is_new_implementation: IS_NEW_IMPLEMENTATION
    };
}

function getRegion(source) {
    return toNA(getDestinationRegion(source.country, source.country_code ?? source.countryCode));
}

function getDestinationFields(properties) {
    return {
        destination_city: toNA(pickString(properties.city)),
        destination_country: toNA(pickString(properties.country)),
        destination_region: getRegion(properties)
    };
}

function getDateFields(properties) {
    return {
        departure_date: toNA(formatDateAs(properties.check_in, DATE_FORMAT_SHORT)),
        return_date: toNA(formatDateAs(properties.check_out, DATE_FORMAT_SHORT)),
        departure_year: toNA(formatDateAs(properties.check_in, DATE_FORMAT_YEAR)),
        departure_month: toNA(formatDateAs(properties.check_in, DATE_FORMAT_MONTH))
    };
}

function getEntrancePoint(properties) {
    return toNA(pickString(properties.entrance_point));
}

function getSearchFields(properties) {
    return {
        entrance_point: getEntrancePoint(properties),
        ...getDestinationFields(properties),
        ...getDateFields(properties)
    };
}

function getTripFields(properties) {
    const rooms = toNumber(properties.rooms);
    return {
        ...getDestinationFields(properties),
        ...getDateFields(properties),
        days_to_departure: toNA(getDaysToTrip(properties.check_in)),
        trip_duration: toNA(getTripLength(properties.check_in, properties.check_out)),
        total_passengers: toNA(getGuestsCount(properties)),
        adults: toNA(getAdultsCount(properties)),
        kids: toNA(getChildrenCount(properties)),
        rooms: toNA(rooms)
    };
}

// GA4 ecommerce needs an ISO currency and numeric amounts, so unknown money fields are omitted
// instead of sent as NA (an agreed exception to the contract's general NA rule).
function getMoney(source) {
    const amount = roundMoney(toNumber(source.total_price ?? source.totalPrice));
    const currency = pickString(source.total_price_currency);
    return amount !== null && currency ? { amount, currency } : null;
}

// The contract asks for the reservation price with quantity 1, so a nightly price is never substituted.
// item_variant trusts only the hotel's own category: stay_type defaults to Hotel and item_varient is the searched place's.
function buildItem(source, index) {
    const city = pickString(source.city);
    const stars = toNumber(source.hotel_stars ?? source.stars);
    const money = getMoney(source);
    return {
        item_name: toNA(pickString(source.item_name, source.hotel_name, source.name)),
        item_id: toNA(pickString(source.item_id, source.hotel_id)),
        item_category: ITEM_CATEGORY_HOTELS,
        item_variant: toNA(pickString(source.hotel_stay_type)),
        ...(stars !== null && { hotel_stars: Math.round(stars) }),
        ...(index && { index }),
        quantity: ITEM_QUANTITY,
        ...(money && { price: money.amount }),
        destination_city: toNA(city),
        destination_country: toNA(pickString(source.country)),
        destination_region: getRegion(source),
        destination: toNA(city)
    };
}

function buildFunnelEvent(eventName, step, properties, ecommerce) {
    return buildElalEvent(eventName, PROCESS_HOTEL_RESERVATION, VALUE_NA, {
        ...step,
        ...getSearchFields(properties),
        ...(ecommerce && { ecommerce })
    });
}

function transformSearchHotel(properties) {
    return buildFunnelEvent('search_hotel', STEP_SEARCH_HOTEL, properties);
}

// A priced hotel without a known total currency, or two currencies, would mislabel the list, so it is omitted.
function getSharedListCurrency(hotels) {
    const pricedCurrencies = hotels
        .filter((hotel) => hotel && toNumber(hotel.total_price ?? hotel.totalPrice) !== null)
        .map((hotel) => pickString(hotel.total_price_currency));
    const currencies = new Set(pricedCurrencies);
    return currencies.size === SINGLE_CURRENCY_COUNT && !currencies.has(null) ? pricedCurrencies[0] : null;
}

function transformViewItemList(properties) {
    const hotels = Array.isArray(properties.hotels) ? properties.hotels.slice(0, MAX_LIST_ITEMS) : [];
    const items = hotels.map((hotel, position) => buildItem(hotel, toNumber(hotel.index) ?? position + 1));
    const currency = getSharedListCurrency(hotels);
    return buildFunnelEvent('view_item_list', STEP_HOTEL_LIST, properties, {
        ...(currency && { currency }),
        item_list_name: ITEM_LIST_HOTELS_RESULTS,
        items
    });
}

// Cards and the hotel page show the display currency, so priced funnel events convert the same way.
function withDisplayedTotal(properties) {
    const displayed = ListVersionService.getDisplayedTotal(
        { totalPrice: properties.total_price ?? properties.totalPrice },
        { displayCurrency: properties.display_currency, displayExchangeRate: properties.display_currency_exchange_rate }
    );
    return displayed ? { ...properties, ...displayed } : properties;
}

// index is the hotel's shown list position (list_index); it is omitted when unknown, e.g. map clicks.
// recommended_index is a recommendation rank, not a position, so it never feeds index.
function transformSelectItem(properties) {
    const source = withDisplayedTotal(properties);
    const money = getMoney(source);
    const listIndex = toNumber(properties.list_index);
    return buildFunnelEvent('select_item', STEP_SELECT_HOTEL, properties, {
        ...(money && { currency: money.currency }),
        item_list_name: properties.selection_source === HOTEL_SELECTION_SOURCE.MAP ? ITEM_LIST_MAP : ITEM_LIST_HOTELS_RESULTS,
        items: [buildItem(source, Number.isSafeInteger(listIndex) && listIndex > 0 ? listIndex : undefined)]
    });
}

function buildValueEcommerce(properties) {
    const source = withDisplayedTotal(properties);
    const money = getMoney(source);
    return {
        ...(money && { currency: money.currency, value: money.amount }),
        items: [buildItem(source, 1)]
    };
}

function transformViewItem(properties) {
    // The hotel page re-reports its view when a recheck finishes; only the landing is a view_item.
    if (properties.view_trigger === HOTEL_VIEW_TRIGGER.RECHECK) return null;
    return buildFunnelEvent('view_item', STEP_VIEW_HOTEL, properties, buildValueEcommerce(properties));
}

function transformAddToCart(properties) {
    return buildFunnelEvent('add_to_cart', STEP_ROOM_SELECT, properties, buildValueEcommerce(properties));
}

function transformBeginCheckout(properties) {
    return buildFunnelEvent('begin_checkout', STEP_PAYMENT, properties, buildValueEcommerce(properties));
}

// The contract's add_payment_info has no top-level search fields, only the card and the ecommerce block.
function transformAddPaymentInfo(properties) {
    const { items, ...money } = buildValueEcommerce(properties);
    return buildElalEvent('add_payment_info', PROCESS_HOTEL_RESERVATION, VALUE_NA, {
        ...STEP_PAYMENT_INFO,
        card_brand: toNA(normalizeCardBrand(properties.card_brand)),
        ecommerce: {
            ...money,
            payment_type: PAYMENT_INFO_TYPES.includes(properties.payment_type) ? properties.payment_type : VALUE_NA,
            items
        }
    });
}

function transformPaymentDeclined(properties) {
    return buildElalEvent('payment_declined', PROCESS_HOTEL_RESERVATION, VALUE_NA, {
        error_type: PAYMENT_DECLINE_TYPES.includes(properties.error_type) ? properties.error_type : VALUE_NA,
        error_text: toNA(sanitizeDeclineText(properties.error_text))
    });
}

function getSortDetail(sortMethod) {
    if (typeof sortMethod !== 'string') return VALUE_NA;
    return SORT_DETAIL_BY_METHOD[sortMethod.trim().toLowerCase()] || VALUE_NA;
}

function getFilterDetail(filterMethod) {
    return FILTER_DETAIL_BY_METHOD[filterMethod] || VALUE_NA;
}

function transformSortResults(properties) {
    return buildElalEvent('sort_results', PROCESS_HOTEL_RESERVATION, getSortDetail(properties.sort_method));
}

function transformFilterInteraction(properties) {
    return buildElalEvent('filter_interaction', PROCESS_HOTEL_RESERVATION, getFilterDetail(properties.filter_method));
}

function transformHotelNameFilter() {
    return buildElalEvent('filter_interaction', PROCESS_HOTEL_RESERVATION, FILTER_DETAIL_HOTEL_NAME);
}

function transformOpenMap() {
    return buildElalEvent('open_map', PROCESS_HOTEL_RESERVATION, VALUE_NA);
}

function transformLoginClicked() {
    return buildElalEvent('login_clicked', PROCESS_LOGIN, VALUE_NA, { entrance_point: ENTRANCE_POINT_TRAVEL });
}

function transformAddCoupon(properties) {
    return buildElalEvent('add_coupon', PROCESS_HOTEL_RESERVATION, pickString(properties.coupon_code));
}

function transformPointsSliderInteraction() {
    return buildElalEvent('points_slider_interaction', PROCESS_HOTEL_RESERVATION, VALUE_NA);
}

function transformTermsClicked(properties) {
    const buttonType = TERMS_BUTTON_TYPES.includes(properties.button_type) ? properties.button_type : null;
    return buildElalEvent('terms_conditions_clicked', PROCESS_HOTEL_RESERVATION, buttonType, {
        component_name: TERMS_COMPONENT_ARBITRIP
    });
}

function transformPopupPresented(properties) {
    return buildElalEvent('popup_presented', PROCESS_HOTEL_RESERVATION, VALUE_NA, {
        popup_name: toNA(pickString(properties.popup_name))
    });
}

function buildPopupClicked(popupName, choice) {
    return buildElalEvent('popup_clicked', PROCESS_HOTEL_RESERVATION, choice, { popup_name: toNA(popupName) });
}

function transformLoginPopupClicked(properties) {
    return buildPopupClicked(POPUP_NAME.MATMID_LOGIN, POPUP_CHOICE_BY_LOGIN_OPTION[properties.selected_option]);
}

function transformRateCheckPopupPresented(properties) {
    return buildElalEvent('popup_presented', PROCESS_HOTEL_RESERVATION, VALUE_NA, {
        popup_name: toNA(pickString(properties.popup_type))
    });
}

function transformRateCheckPopupClicked(properties) {
    return buildPopupClicked(pickString(properties.popup_type), pickString(properties.action));
}

function transformPopupClicked(properties) {
    return buildPopupClicked(pickString(properties.popup_name), pickString(properties.choice));
}

function transformOpenBookingInfo() {
    return buildElalEvent('open_booking_info', PROCESS_MY_HOTELS, VALUE_NA);
}

// The booking panel has no CMS document/path metadata, so those contract fields are static NA.
function buildBookingPanelClicked(process, eventDetail, extraFields = {}) {
    return buildElalEvent('component_clicked', process, eventDetail, {
        ...extraFields,
        document_type: VALUE_NA,
        component_name: COMPONENT_BOOKING_PANEL,
        component_path: VALUE_NA
    });
}

function transformBookingPanelComponentClicked(properties) {
    const component = BOOKING_PANEL_COMPONENTS.includes(properties.component_name) ? properties.component_name : null;
    return buildBookingPanelClicked(PROCESS_MY_HOTELS, component);
}

function transformPayNowClicked(properties) {
    return buildBookingPanelClicked(PROCESS_EARLY_PAYMENT, EVENT_DETAIL_PAY_NOW, {
        ...STEP_PAY_NOW,
        booking_id: toNA(pickString(properties.booking_id))
    });
}

function sanitizeErrorText(errorText) {
    const text = pickString(errorText);
    if (!text) return null;
    const trimmed = text.trim();
    return RE_SENSITIVE_ERROR_TEXT.test(trimmed) ? null : trimmed.slice(0, MAX_ERROR_TEXT_LENGTH);
}

function buildErrorEvent({ errorType, errorText, process, step }) {
    return buildElalEvent('error', PAGE_PROCESSES.includes(process) ? process : VALUE_NA, VALUE_NA, {
        error_type: ERROR_TYPES.includes(errorType) ? errorType : VALUE_NA,
        error_text: toNA(sanitizeErrorText(errorText)),
        ...STEP_BY_FUNNEL_STEP[step]
    });
}

function transformErrorDisplayed(properties) {
    return buildErrorEvent({
        errorType: properties.error_type,
        errorText: properties.error_text,
        process: properties.error_process,
        step: properties.error_step
    });
}

// The contract excludes empty required fields; user_input is only used for that check and never forwarded.
function transformFieldError(properties) {
    if (!isNonEmptyFieldValue(properties.user_input)) return null;
    const fieldType = pickString(properties.field_type);
    return buildErrorEvent({
        errorType: ERROR_TYPE.VALIDATION,
        errorText: fieldType && getInvalidFieldErrorText(fieldType),
        process: PROCESS_HOTEL_RESERVATION,
        step: FUNNEL_STEP.PAYMENT
    });
}

function getLanguageName(language) {
    const code = pickString(language);
    return toNA(code && LANGUAGE_NAME_BY_CODE[code.trim().toLowerCase()]);
}

// No trusted source exists yet for the user's country, so user_country is always NA.
function transformPageViewed(properties) {
    const process = PAGE_PROCESSES.includes(properties.page_process) ? properties.page_process : null;
    return {
        event: DL_PAGE,
        page_type: toNA(pickString(properties.page_type)),
        language: getLanguageName(properties.language),
        user_country: VALUE_NA,
        group: GROUP_ELAL_TRAVEL,
        process: toNA(process),
        ...(process === PROCESS_HOTEL_RESERVATION && getTripFields(properties)),
        entrance_point: getEntrancePoint(properties),
        is_new_implementation: IS_NEW_IMPLEMENTATION
    };
}

function getPointsBalance(value) {
    const points = toNumber(value);
    return points !== null && points >= 0 ? points : null;
}

function getPointsRange(points) {
    if (points === null) return VALUE_NA;
    if (points === 0) return POINTS_RANGE_ZERO;
    const range = POINTS_RANGES.find(({ max }) => points <= max);
    return range ? range.label : POINTS_RANGE_TOP;
}

// User data is a data-model push without `event`; logged-out users always reset every identity field.
function transformUserDataUpdated(properties) {
    if (properties.logged_in !== true) {
        return {
            matmid_id: VALUE_NA,
            logged_in: false,
            matmid_tier: VALUE_NA,
            user_points: VALUE_NA,
            user_points_range: VALUE_NA,
            member_type: VALUE_NA
        };
    }

    const matmidId = hashMatmidId(properties.matmid_id);
    const points = getPointsBalance(properties.points_balance);
    const isMember = properties.loyalty === true || !!matmidId;
    return {
        matmid_id: toNA(matmidId),
        logged_in: true,
        matmid_tier: toNA(pickString(properties.customer_tier)),
        user_points: toNA(points),
        user_points_range: getPointsRange(points),
        member_type: isMember ? MEMBER_TYPE_MATMID : VALUE_NA
    };
}

function getRevenueItem(snapshot) {
    const region = getDestinationRegion(snapshot.destination_country, snapshot.destination_country_code);
    return {
        item_name: toNA(pickString(snapshot.hotel_name)),
        item_id: toNA(pickString(snapshot.hotel_id)),
        item_category: ITEM_CATEGORY_HOTELS,
        item_variant: toNA(pickString(snapshot.hotel_stay_type)),
        hotel_stars: Number.isSafeInteger(snapshot.hotel_stars) && snapshot.hotel_stars >= 0 ? snapshot.hotel_stars : VALUE_NA,
        quantity: ITEM_QUANTITY,
        price: snapshot.item_price,
        destination_city: toNA(pickString(snapshot.destination_city)),
        destination_country: toNA(pickString(snapshot.destination_country)),
        destination_region: toNA(region),
        destination: toNA(pickString(snapshot.destination_city)),
    };
}

function getRevenueEcommerce(snapshot, includeCoupon) {
    return {
        transaction_id: snapshot.transaction_id,
        currency: 'USD',
        value: snapshot.value,
        ...(includeCoupon && typeof snapshot.coupon === 'string' && snapshot.coupon.trim() && { coupon: snapshot.coupon.trim() }),
        items: [getRevenueItem(snapshot)],
    };
}

function transformConfirmedBookingViewed(properties) {
    const { snapshot } = properties;
    if (!RevenueService.isValidRevenueSnapshot(snapshot)) return null;
    const points = Number.isFinite(properties.points_balance) && properties.points_balance >= 0 ? properties.points_balance : null;
    const rooms = Number.isSafeInteger(snapshot.rooms) && snapshot.rooms > 0 ? snapshot.rooms : null;
    const adults = rooms && Number.isSafeInteger(snapshot.adults_per_room) && snapshot.adults_per_room >= 0 ? snapshot.adults_per_room * rooms : null;
    const kids = rooms && Number.isSafeInteger(snapshot.kids_per_room) && snapshot.kids_per_room >= 0 ? snapshot.kids_per_room * rooms : null;
    const item = getRevenueItem(snapshot);
    return buildElalEvent('purchase', PROCESS_HOTEL_RESERVATION, VALUE_NA, {
        ...STEP_CONFIRMATION,
        user_points: toNA(points),
        user_points_range: getPointsRange(points),
        order_points_value: snapshot.order_points_value,
        usd_to_pay: snapshot.usd_to_pay,
        points_spent: snapshot.points_spent,
        points_cash_value: snapshot.points_cash_value,
        voucher_cash_value: snapshot.voucher_cash_value,
        total_passengers: toNA(adults !== null && kids !== null ? adults + kids : null),
        adults: toNA(adults),
        kids: toNA(kids),
        trip_duration: toNA(getTripLength(snapshot.check_in, snapshot.check_out)),
        ...getDateFields(snapshot),
        days_to_departure: toNA(getDaysToTrip(snapshot.check_in)),
        hashed_email: RevenueService.sanitizedHash(snapshot.hashed_email),
        hashed_phone: RevenueService.sanitizedHash(snapshot.hashed_phone),
        free_cancellation_until: formatDateAs(snapshot.free_cancellation_until, DATE_FORMAT_SHORT) || 'no_free_cancellation',
        bnpl: 'pay_now',
        transaction_type: 'hotel',
        entrance_point: VALUE_NA,
        destination_city: item.destination_city,
        destination_country: item.destination_country,
        destination_region: item.destination_region,
        ecommerce: getRevenueEcommerce(snapshot, true),
    });
}

function transformApprovedReservationCancellation(properties) {
    const { snapshot } = properties;
    if (!RevenueService.isValidRevenueSnapshot(snapshot)) return null;
    return buildElalEvent('refund', PROCESS_MY_HOTELS, VALUE_NA, {
        transaction_type: 'hotel',
        ecommerce: getRevenueEcommerce(snapshot, false),
    });
}

// EXECUTED_SEARCH stays unmapped: it fires on the web Search click and on mobile results initialisation
// (incl. deep links/restores); SEARCH_RESULTS_LANDED marks arrival on results after Search.
const EVENT_TRANSFORMERS = {
    PAGE_VIEWED: transformPageViewed,
    USER_DATA_UPDATED: transformUserDataUpdated,
    CONFIRMED_BOOKING_VIEWED: transformConfirmedBookingViewed,
    APPROVED_RESERVATION_CANCELLATION: transformApprovedReservationCancellation,
    SEARCH_RESULTS_LANDED: transformSearchHotel,
    RESULTS_RECEIVED_HOTELS: transformViewItemList,
    SELECTED_HOTEL: transformSelectItem,
    VIEWED_HOTEL_PAGE: transformViewItem,
    SELECTED_DEAL: transformAddToCart,
    SELECTED_LAZY_RECHECK_DEAL: transformAddToCart,
    PROCEEDED_TO_PAYMENT_OPEN_PAYMENT_FORM: transformBeginCheckout,
    PAYMENT_METHOD_ADDED: transformAddPaymentInfo,
    PAYMENT_DECLINED: transformPaymentDeclined,
    RESULTS_SORTED: transformSortResults,
    RESULTS_FILTERED: transformFilterInteraction,
    HOTEL_SUGGESTION_SELECTED: transformHotelNameFilter,
    OPEN_RESULTS_MAP: transformOpenMap,
    OPEN_HOTELS_MAP: transformOpenMap,
    LOGIN_CLICKED: transformLoginClicked,
    COUPON_ACCEPTED: transformAddCoupon,
    POINTS_SLIDER_INTERACTION: transformPointsSliderInteraction,
    TERMS_CONDITIONS_CLICKED: transformTermsClicked,
    POPUP_PRESENTED: transformPopupPresented,
    SELECTED_LOGIN_OPTION: transformLoginPopupClicked,
    ROOM_RATE_CHECK_POPUP_SHOWN: transformRateCheckPopupPresented,
    ROOM_RATE_CHECK_POPUP_ACTION: transformRateCheckPopupClicked,
    OPEN_BOOKINGS_SIDEPANEL: transformOpenBookingInfo,
    OPEN_BOOKINGS_ITEM: transformOpenBookingInfo,
    POPUP_CLICKED: transformPopupClicked,
    BOOKING_PANEL_COMPONENT_CLICKED: transformBookingPanelComponentClicked,
    PAY_NOW_CLICKED: transformPayNowClicked,
    ERROR_DISPLAYED: transformErrorDisplayed,
    CONTACT_FIELD_ERROR: transformFieldError,
    TRAVELER_FIELD_ERROR: transformFieldError
};

export function mapEventToElalDataLayer(event_name, properties = {}) {
    const transformer = EVENT_TRANSFORMERS[event_name];
    return transformer ? transformer(properties || {}) : null;
}

export function getElalPayloadKind(payload) {
    if (!payload) return null;
    if (payload.event === DL_PAGE) return ELAL_PAYLOAD_KIND.PAGE;
    if (payload.event === DL_EVENT) return ELAL_PAYLOAD_KIND.EVENT;
    return ELAL_PAYLOAD_KIND.USER_DATA;
}

// Native WebView receives the same params without the dataLayer `event` key (PDF p4-5).
export function toElalFirebaseEvent(elalEvent) {
    if (!elalEvent) return null;
    const { event: _event, event_name, ecommerce, ...params } = elalEvent;
    return { name: event_name, params: { ...params, ...(ecommerce || {}) } };
}

function toStringParams(params) {
    return Object.fromEntries(Object.entries(params).map(([key, value]) => [key, String(value)]));
}

// Builds the full postMessage body: pages become screen_view, user data uses its own message type.
export function toElalNativeMessage(payload) {
    const kind = getElalPayloadKind(payload);
    if (kind === ELAL_PAYLOAD_KIND.PAGE) {
        const { event: _event, ...params } = payload;
        return { type: NATIVE_MESSAGE_TYPE_EVENT, payload: { name: NATIVE_SCREEN_VIEW, params } };
    }
    if (kind === ELAL_PAYLOAD_KIND.USER_DATA) {
        return { type: NATIVE_MESSAGE_TYPE_USER_DATA, payload: { params: toStringParams(payload) } };
    }
    if (kind === ELAL_PAYLOAD_KIND.EVENT) {
        return { type: NATIVE_MESSAGE_TYPE_EVENT, payload: toElalFirebaseEvent(payload) };
    }
    return null;
}

export { ELAL_PAYLOAD_KIND };
